import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const GreetingMessageSettings = () => {
  const [config, setConfig] = useState({
    enabled: true,
    message: '',
    use_default: true
  });
  const [preview, setPreview] = useState('');
  const [variables, setVariables] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/whatsapp/greeting`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setConfig(response.data);
      setPreview(response.data.preview);
      setVariables(response.data.variables || {});
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
      setMessage('Erro ao carregar configuração');
    } finally {
      setLoading(false);
    }
  };

  const handleMessageChange = async (e) => {
    const newMessage = e.target.value;
    setConfig({ ...config, message: newMessage });

    // Gerar preview (debounce)
    if (newMessage) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(`${API}/whatsapp/greeting/preview`, 
          { message: newMessage },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setPreview(response.data.preview);
      } catch (error) {
        console.error('Erro ao gerar preview:', error);
      }
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      await axios.put(`${API}/whatsapp/greeting`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessage('✅ Configuração salva com sucesso!');
      setTimeout(() => setMessage(''), 3000);
      await loadConfig();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      setMessage('❌ Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (window.confirm('Resetar para mensagem padrão?')) {
      try {
        setSaving(true);
        const token = localStorage.getItem('token');
        
        const response = await axios.post(`${API}/whatsapp/greeting/reset`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setConfig({ 
          ...config, 
          message: response.data.default_message,
          use_default: true
        });
        setPreview(response.data.default_message);
        setMessage('✅ Mensagem resetada para o padrão!');
        setTimeout(() => setMessage(''), 3000);
      } catch (error) {
        console.error('Erro ao resetar:', error);
        setMessage('❌ Erro ao resetar');
      } finally {
        setSaving(false);
      }
    }
  };

  const insertVariable = (variable) => {
    const newMessage = config.message + variable;
    setConfig({ ...config, message: newMessage });
    handleMessageChange({ target: { value: newMessage } });
  };

  if (loading) {
    return <div className="text-center py-8">Carregando...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">💬 Mensagem de Saudação Automática</h2>
      
      {message && (
        <div className={`mb-4 p-3 rounded ${message.includes('✅') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {message}
        </div>
      )}

      <div className="mb-6">
        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={config.enabled}
            onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
            className="w-5 h-5"
          />
          <span className="font-medium">Habilitar mensagem de saudação automática</span>
        </label>
        <p className="text-sm text-gray-600 mt-2">
          Quando habilitada, essa mensagem será enviada automaticamente no primeiro contato do dia com cada cliente.
        </p>
      </div>

      <div className="mb-6">
        <label className="block font-medium mb-2">Mensagem Personalizada:</label>
        <textarea
          value={config.message}
          onChange={handleMessageChange}
          disabled={!config.enabled}
          rows="6"
          maxLength="500"
          className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:bg-gray-100"
          placeholder="Digite sua mensagem de saudação..."
        />
        <div className="flex justify-between items-center mt-2 text-sm text-gray-600">
          <span>{config.message.length}/500 caracteres</span>
        </div>
      </div>

      <div className="mb-6">
        <label className="block font-medium mb-2">📝 Variáveis Disponíveis:</label>
        <div className="flex flex-wrap gap-2">
          {[
            '{nome_loja}',
            '{nome_cliente}',
            '{link_cardapio}',
            '{telefone}',
            '{horario}',
            '{endereco}'
          ].map(variable => (
            <button
              key={variable}
              onClick={() => insertVariable(variable)}
              disabled={!config.enabled}
              className="px-3 py-1 bg-blue-100 text-blue-800 rounded-md text-sm hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {variable}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Clique para inserir a variável na mensagem. Ela será substituída automaticamente pelo valor real.
        </p>
      </div>

      <div className="mb-6">
        <label className="block font-medium mb-2">👁️ Preview:</label>
        <div className="bg-gray-50 border border-gray-300 rounded-lg p-4 whitespace-pre-wrap">
          {preview || 'Digite uma mensagem para ver o preview...'}
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={handleSave}
          disabled={!config.enabled || saving}
          className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {saving ? '⏳ Salvando...' : '💾 Salvar Alterações'}
        </button>
        
        <button
          onClick={handleReset}
          disabled={saving}
          className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          🔄 Usar Padrão
        </button>
      </div>

      <div className="mt-6 p-4 bg-emerald-50 border-l-4 border-emerald-500 rounded">
        <h3 className="font-semibold text-blue-900 mb-2">ℹ️ Como funciona:</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• A saudação é enviada <strong>uma vez por dia</strong> no primeiro contato do cliente</li>
          <li>• Use as variáveis para personalizar a mensagem automaticamente</li>
          <li>• Máximo de 500 caracteres</li>
          <li>• A mensagem padrão inclui o link do seu cardápio digital</li>
        </ul>
      </div>
    </div>
  );
};

export default GreetingMessageSettings;
